import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import { Check, Loader2, KeyRound, ArrowLeft } from "lucide-react";

const origin = () => window.location.origin;

export default function Offers() {
  const [loading, setLoading] = useState("");
  const [code, setCode] = useState("");
  const [restoreMsg, setRestoreMsg] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const buy = async (offer) => {
    setLoading(offer);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, { offer, origin_url: origin() });
      window.location.href = data.checkout_url;
    } catch (e) {
      setLoading("");
      alert("We couldn't open checkout just now. Please try again in a moment.");
    }
  };

  const restore = async (e) => {
    e.preventDefault();
    setRestoring(true);
    setRestoreMsg(null);
    try {
      const { data } = await axios.post(`${API}/entitlement/restore`, { code });
      localStorage.setItem("bnm-entitlement", JSON.stringify(data));
      setRestoreMsg({ ok: true, text: "Welcome back. Your access has been restored." });
    } catch (e) {
      setRestoreMsg({ ok: false, text: "That access code wasn't found. Check it and try again." });
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="min-h-[100dvh] watercolor-wash">
      <div className="mx-auto max-w-2xl px-5 py-12 safe-top safe-bottom safe-x sm:px-8">
        <Link to="/" data-testid="offers-back" className="mb-8 inline-flex items-center gap-2 font-display text-sm text-ink-soft hover:text-gold-deep">
          <ArrowLeft className="h-4 w-4" /> Back to the book
        </Link>

        <p className="kicker text-ink-muted">The Threshold of the Exhale</p>
        <h1 className="mt-2 font-display text-4xl text-ink sm:text-5xl">You do not have to hold the rest alone.</h1>
        <p className="mt-4 text-[15px] leading-relaxed text-ink-soft">
          You have met the first door. What follows is not another manual of expectations, but a
          systematic, biopsychosocial map for setting down the quiet endurance your body has held for
          years. To honor the weight and preservation of this work, the complete volume of
          <em> Braced No More</em> is available for you to carry forward.
        </p>

        <div className="mt-10 grid gap-5">
          {/* Bundle — recommended */}
          <div className="rounded-2xl bg-ivory-50 p-6 shadow-sm ring-2 ring-gold/40" data-testid="offer-bundle">
            <div className="mb-1 flex items-center justify-between">
              <h2 className="font-display text-xl text-ink">The Integrated Sanctuary</h2>
              <span className="rounded-full bg-gold/15 px-3 py-1 text-[11px] uppercase tracking-[0.12em] text-gold-deep">Recommended</span>
            </div>
            <p className="text-sm text-ink-soft">The complete book, plus The Language Vault — ongoing somatic reframes and audio reflections from Casey Muze.</p>
            <ul className="mt-3 space-y-1.5 text-sm text-ink-soft">
              {["Lifetime access to the full book", "The Language Vault — $15 first month, then $25/mo", "Cancel the Vault anytime"].map((t) => (
                <li key={t} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 flex-none text-gold-deep" />{t}</li>
              ))}
            </ul>
            <p className="mt-4 font-display text-lg text-ink">$33.99 book + $15.00 today</p>
            <button type="button" onClick={() => buy("bundle")} disabled={!!loading} data-testid="buy-bundle"
              className="debossed-btn mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 font-display text-ink disabled:opacity-50">
              {loading === "bundle" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Begin the Integrated Sanctuary
            </button>
          </div>

          {/* Book only */}
          <div className="rounded-2xl bg-ivory-50 p-6 shadow-sm ring-1 ring-ivory-400/60" data-testid="offer-book">
            <h2 className="font-display text-xl text-ink">The Complete Book</h2>
            <p className="text-sm text-ink-soft">Immediate lifetime digital access to all chapters, reflective prompts and somatic pauses.</p>
            <p className="mt-4 font-display text-lg text-ink">$33.99 <span className="text-sm text-ink-muted">· one-time</span></p>
            <button type="button" onClick={() => buy("book")} disabled={!!loading} data-testid="buy-book"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-ink/20 px-6 py-3 font-display text-ink transition hover:border-gold/50 disabled:opacity-50">
              {loading === "book" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Carry the full book forward
            </button>
          </div>
        </div>

        {/* Restore */}
        <form onSubmit={restore} className="mt-12 rounded-2xl border border-ivory-400/60 p-5" data-testid="restore-form">
          <p className="mb-2 inline-flex items-center gap-2 font-display text-sm text-ink"><KeyRound className="h-4 w-4 text-gold-deep" /> Already have an access code?</p>
          <div className="flex flex-wrap gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="BNM-XXXX-XXXX" data-testid="restore-input"
              className="min-w-[180px] flex-1 rounded-lg border border-ivory-400/70 bg-[#FCFBF7] px-3 py-2.5 text-ink focus:outline-none focus:ring-1 focus:ring-gold/50" />
            <button type="submit" disabled={restoring || !code} data-testid="restore-submit"
              className="rounded-full bg-ink px-5 py-2.5 font-display text-ivory-50 disabled:opacity-50">Restore</button>
          </div>
          {restoreMsg && <p className={`mt-2 text-sm ${restoreMsg.ok ? "text-emerald-600" : "text-terracotta-deep"}`} data-testid="restore-msg">{restoreMsg.text}</p>}
        </form>
      </div>
    </div>
  );
}
